import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { useSearch } from '@/hooks/use-search';
import { useTagCreate } from '@/hooks/rxdb-models';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Button } from '@flintmint/web-shared/components/button';
import { ColorPicker } from '@flintmint/web-shared/components/color-picker';
import { Dropdown, type DropdownProps } from '@flintmint/web-shared/components/dropdown';

import { getSortModel } from '@/utils/helper-utils';
import { animateScaleAndOpacity, tagDefaultColor } from '@flintmint/web-shared/constants/ui';

import styles from './tag-dropdown.module.scss';

export const TagDropdown = (props: TagDropdownProps) => {
  const { actAs, onClose, attachedIds = [], ...rest } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [newTagColor, setNewTagColor] = useState(tagDefaultColor);
  const [selections, setSelections] = useState<string[]>(attachedIds);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: createTag } = useTagCreate();

  useEffect(() => {
    if (!open) {
      setSelections(attachedIds);
    }
  }, [attachedIds, open]);

  const { data } = useSearch(
    {
      term: search,
      where: {
        types: ['tags'],
      },
      tolerance: 0.5,
      sort: getSortModel({ key: 'createdAt', direction: 'desc' }),
    },
    open
  );

  const returnFocusToInput = () => requestAnimationFrame(() => inputRef.current?.focus());

  return (
    <Dropdown
      {...rest}
      search={search}
      selections={selections}
      onSearchChange={setSearch}
      onConfirm={() => {
        return createTag(
          {
            name: search,
            color: newTagColor,
          },
          {
            onSuccess: (newTag) => {
              setSelections([...selections, newTag.id]);
              setOpenConfirmation(false);
              setSearch('');
            },
          }
        );
      }}
      showConfirmation={openConfirmation}
      onOpen={() => {
        setOpen(true);
        setSelections(attachedIds);
      }}
      noDataActionLabel="Create tag"
      onConfirmationOpen={() => setOpenConfirmation(true)}
      placeholder={openConfirmation ? 'Add new item' : 'Search Tags'}
      onConfirmationClose={() => setOpenConfirmation(false)}
      data={data.map((el) => ({ ...el.document, color: el.document.content }))}
      onClose={() => {
        setSearch('');
        !isEqual(selections, attachedIds) && onClose?.(selections);
        setOpen(false);
        setOpenConfirmation(false);
      }}
      onSelectionChange={setSelections}
      trigger={
        actAs === 'filter' ? (
          <div className={styles.tagFilterWithBadge} data-testid="tags-filter-icon">
            <AnimatePresence>
              {!!attachedIds.length && (
                <motion.small data-testid="tags-badge" {...animateScaleAndOpacity}>
                  {attachedIds.length}
                </motion.small>
              )}
            </AnimatePresence>
            <Icon size={20} type="filter-1" />
          </div>
        ) : (
          props.trigger
        )
      }
      inputProps={{
        inputRef,
        left: (
          <AnimatePresence mode="wait" initial={false}>
            {openConfirmation ? (
              <motion.div key="tag" data-testid="color-indicator" {...animateScaleAndOpacity}>
                <ColorPicker
                  indicatorSize="big"
                  color={newTagColor}
                  onChange={setNewTagColor}
                  onAnimationComplete={returnFocusToInput}
                />
              </motion.div>
            ) : (
              <motion.div key="icon" {...animateScaleAndOpacity}>
                <Icon type="search-01" size={20} />
              </motion.div>
            )}
          </AnimatePresence>
        ),
        right: (
          <Button
            iconOnly
            size="small"
            color="light"
            cornerRadius="rounded"
            data-testid="create-new-btn"
            onClick={() => {
              setOpenConfirmation(true);
              returnFocusToInput();
            }}
          >
            <Icon type="plus" size={16} />
          </Button>
        ),
      }}
    />
  );
};

export interface TagDropdownProps extends Omit<Partial<DropdownProps>, 'onClose'> {
  actAs?: 'filter';
  attachedIds?: string[];
  onClose?: (selections: string[]) => void;
}
